import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Leaf, PiggyBank, ShieldCheck } from "lucide-react";
import { waLink } from "@/lib/api";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const badges = [
  { icon: Leaf, text: "Metodo 100% Ecosostenibile" },
  { icon: PiggyBank, text: "Risparmio medio 350€" },
  { icon: Phone, text: "Chiamata diretta col Dottore" },
];

const PORTRAIT = "/dott-vincenti.jpg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

  return (
    <section id="top" ref={ref} className="relative max-w-6xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-x-clip">
      <div className="hidden sm:block absolute top-8 right-6 lg:top-12 lg:right-10 z-10 rounded-2xl bg-terracotta px-4 py-3 shadow-lg rotate-3">
        <p className="font-serif text-2xl font-bold text-alabaster leading-none">70%</p>
        <p className="text-[10px] font-mono uppercase tracking-widest text-alabaster/90 mt-1">
          di risparmio
        </p>
      </div>
      <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="col-span-12 lg:col-span-8 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-sage px-4 py-1.5 mb-8 max-w-full"
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mosslight opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-mosslight" />
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-sagetext font-semibold whitespace-normal">
              Disponibile 7/7 dalle 7:00 alle 24:00
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.12] text-moss"
          >
            <RevealLine delay={0.15}>Risolvi l'infestazione</RevealLine>
            <RevealLine delay={0.3}>
              <em className="text-terracotta not-italic font-medium">da solo</em>, con metodo
            </RevealLine>
            <RevealLine delay={0.45}>
              scientifico. <span className="italic text-mosslight">Spendendo il minimo.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-sub"
          >
            Una ditta di disinfestazione chiede 250-600€ a intervento. Con una chiamata
            di 10-20 minuti ti dico esattamente quale prodotto professionale comprare
            in agraria oppure online e come applicarlo. Soluzioni fai da te facili e immediate sotto la guida di professionisti esperti.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pacchetti"
              data-testid="hero-cta-buy-credits"
              className="rounded-full bg-moss px-8 py-4 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-mosslight hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(27,59,43,0.25)]"
            >
              Acquista un Voucher — da 60€
            </a>
            <a
              href={waLink("Buongiorno Dott. Vincenti, vorrei informazioni prima di acquistare una consulenza di disinfestazione.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-moss/20 px-7 py-[14px] text-base font-semibold text-moss transition-all duration-200 hover:border-moss hover:-translate-y-0.5"
            >
              <MessageCircle size={18} />
              Scrivimi su WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {badges.map(({ icon: Icon, text }) => (
              <span
                key={text}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-line px-4 py-2 text-xs font-semibold text-sagetext"
              >
                <Icon size={14} className="text-terracotta" />
                {text}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y, rotate }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-4 relative min-w-0"
        >
          <div className="absolute -inset-3 rounded-[2rem] border border-gold/40 rotate-2" />
          <div className="relative overflow-hidden rounded-[1.75rem] shadow-[0_30px_60px_rgba(15,23,20,0.25)]">
            <img
              src={PORTRAIT}
              alt="Dott. Giuseppe Vincenti"
              className="w-full aspect-[558/551] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-mossdeep/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="font-serif text-xl text-alabaster font-semibold">
                Per. Agr. Dott. Giuseppe Vincenti
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mt-1 font-semibold">
                Tecnico esperto in difesa ambientale
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
